import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  Boxes,
  Database,
  EyeOff,
  History,
  Layers,
  Megaphone,
  Plus,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  TriangleAlert,
  ArrowUpCircle,
} from 'lucide-react';
import { modpackConfigsApiService } from '../services/modpackConfigsApi';
import {
  ModpackConfig,
  ModpackConfigEnv,
  ModpackConfigVocabularies,
  UNCLUSTERED,
} from '../types/modpackConfig';
import { getModpackConfigPerms } from '../utils/modpackConfigPermissions';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { useFleetStatus, liveFor } from '../hooks/useFleetStatus';
import type { FleetStatus } from '../hooks/useFleetStatus';
import { PanelTour } from '../components/ui/PanelTour';
import { modpackConfigsPanelTour } from '../config/panelTours';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreateModpackConfigModal } from '../components/modpackconfigs/CreateModpackConfigModal';
import { UpdatePackModal } from '../components/modpackconfigs/UpdatePackModal';
import { AnnounceLaunchModal } from '../components/modpackconfigs/AnnounceLaunchModal';
import { RecordWipeModal } from '../components/modpackconfigs/RecordWipeModal';
import { HiddenBadge, LastWipe } from '../components/modpackconfigs/ModpackConfigBadges';
import { ClusterLiveSummary, LiveStatusBadge, describeLive } from '../components/modpackconfigs/LiveStatus';
import { statusMonitorPathFor } from '../components/modpackconfigs/statusPaths';
import { isHidden } from '../components/modpackconfigs/configFields';
import { cn } from '../utils/cn';

const ALL = '__all__';

/**
 * What the network says about a server, as a filter. "Hidden" is the one
 * state the config itself records; the rest come from the server list and
 * the maintenance flag, the same feeds the status page reads.
 */
type LiveFilter = typeof ALL | 'online' | 'offline' | 'maintenance' | 'coming_soon' | 'unlisted' | 'hidden' | 'visible';

export const ModpackConfigsView = () => {
  useDocumentTitle('Modpack Configs');

  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getModpackConfigPerms(identity, isDev), [identity, isDev]);

  const { tourNavigation } = useTourNavigation();
  // The status page links here with ?q=<cluster>, so its "Configs" lands on
  // exactly those rows. Read once; the page owns the search after.
  const [params] = useSearchParams();

  const [env, setEnv] = useState<ModpackConfigEnv>('production');
  const [configs, setConfigs] = useState<ModpackConfig[]>([]);
  const [vocabularies, setVocabularies] = useState<ModpackConfigVocabularies | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState(() => params.get('q') ?? '');
  const debouncedSearch = useDebounce(search, 300);
  const [cluster, setCluster] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [modloader, setModloader] = useState(ALL);
  const [live, setLive] = useState<LiveFilter>(ALL);
  const [grouping, setGrouping] = useState<'cluster' | 'flat'>('cluster');

  const [creating, setCreating] = useState(false);
  // Cluster-level actions launched from the list, keyed by the cluster's head.
  const [updating, setUpdating] = useState<ClusterGroup | null>(null);
  const [wiping, setWiping] = useState<ClusterGroup | null>(null);
  const [announcingLaunch, setAnnouncingLaunch] = useState<ClusterGroup | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncConfirm, setSyncConfirm] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Liveness and maintenance, joined by address. The dev table mirrors the
  // live addresses, so the readings are just as true there.
  const fleet = useFleetStatus({ enabled: perms.canView });
  const liveOf = useCallback((c: ModpackConfig) => liveFor(fleet, c.server_address), [fleet]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, vocab] = await Promise.all([
        modpackConfigsApiService.listConfigs(env),
        modpackConfigsApiService.getVocabularies(env),
      ]);
      setConfigs(list.results);
      setVocabularies(vocab);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load modpack configs');
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    load();
  }, [load]);

  // Switching environment changes which rows the filters refer to, so a cluster
  // chosen in production would silently match nothing in dev.
  useEffect(() => {
    setCluster(ALL);
  }, [env]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return configs.filter((c) => {
      if (cluster !== ALL && String(c.cluster_parent ?? '') !== cluster) return false;
      if (region !== ALL && (c.region_name ?? '') !== region) return false;
      if (modloader !== ALL && (c.modloader ?? '') !== modloader) return false;
      if (live === 'hidden' && !isHidden(c)) return false;
      if (live === 'visible' && isHidden(c)) return false;
      if (live === 'online' || live === 'offline' || live === 'maintenance' || live === 'coming_soon' || live === 'unlisted') {
        if (describeLive(liveOf(c)).state !== live) return false;
      }
      if (!term) return true;
      return [
        c.server_address,
        c.modpack_name,
        c.modpack_full_name,
        c.cluster_name,
        c.author,
        c.amp_name,
        c.modpack_version,
        c.region_name,
      ].some((v) => (v ?? '').toLowerCase().includes(term));
    });
  }, [configs, debouncedSearch, cluster, region, modloader, live, liveOf]);

  /**
   * Servers grouped by the cluster they belong to, head first, standalone packs
   * last. A cluster is identified by cluster_parent, not by cluster_name --
   * several unrelated rows carry the sentinel 'default'.
   */
  const grouped = useMemo(() => {
    const byParent = new Map<number, ModpackConfig[]>();
    const standalone: ModpackConfig[] = [];
    for (const c of filtered) {
      const isStandalone = !c.cluster_name || c.cluster_name === UNCLUSTERED || c.cluster_parent === null;
      if (isStandalone) { standalone.push(c); continue; }
      const key = c.cluster_parent as number;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(c);
    }
    const clusters = [...byParent.entries()]
      .map(([parent, members]) => {
        const head = members.find((m) => m.id === parent) ?? null;
        // Head first, then the rest by address.
        const rest = members.filter((m) => m.id !== parent).sort((a, b) => a.server_address.localeCompare(b.server_address));
        return {
          parent,
          name: head?.cluster_name ?? members[0].cluster_name ?? `Cluster ${parent}`,
          head,
          members: head ? [head, ...rest] : rest,
          versions: [...new Set(members.map((m) => m.modpack_version ?? '—'))],
        };
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    return { clusters, standalone: standalone.sort((a, b) => a.server_address.localeCompare(b.server_address)) };
  }, [filtered]);

  /**
   * The actions offered on each cluster heading. Update and wipe need write;
   * the launch announcement creates a public post, so it needs admin, and
   * none of them makes sense against the dev table's Discord side.
   */
  const clusterActions = useMemo<ClusterActions | undefined>(() => {
    if (!perms.canWrite) return undefined;
    return {
      onUpdate: (group) => setUpdating(group),
      onWipe: (group) => setWiping(group),
      ...(perms.canAdmin && env === 'production'
        ? { onAnnounceLaunch: (group: ClusterGroup) => setAnnouncingLaunch(group) }
        : {}),
    };
  }, [perms.canWrite, perms.canAdmin, env]);

  const stats = useMemo(() => {
    const clusters = new Set(configs.map((c) => c.cluster_parent).filter((v) => v !== null));
    const states = configs.map((c) => describeLive(liveOf(c)).state);
    return {
      total: configs.length,
      hidden: configs.filter(isHidden).length,
      clusters: clusters.size,
      online: states.filter((s) => s === 'online').length,
      listed: states.filter((s) => s === 'online' || s === 'offline' || s === 'maintenance' || s === 'coming_soon').length,
      maintenance: states.filter((s) => s === 'maintenance').length,
      comingSoon: states.filter((s) => s === 'coming_soon').length,
      offline: states.filter((s) => s === 'offline').length,
      loaded: fleet.listings !== null && fleet.maintenance !== null,
    };
  }, [configs, liveOf, fleet.listings, fleet.maintenance]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const result = await modpackConfigsApiService.syncDevFromProduction();
      setSyncConfirm(false);
      setNotice(
        `Development replaced: ${result.replaced} row${result.replaced === 1 ? '' : 's'} removed, ` +
        `${result.copied} copied from production.`,
      );
      if (env === 'development') await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync the dev table');
      setSyncConfirm(false);
    } finally {
      setSyncing(false);
    }
  };

  if (!perms.canView) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Infrastructure" title="Modpack Configs" icon={Server} />
        <EmptyState
          tone="warning"
          icon={ShieldCheck}
          title="You don't have permission to view modpack configs"
          description={
            <>Ask an admin for the <code className="font-mono text-xs">argus.modpackconfigs.read</code> permission.</>
          }
        />
      </div>
    );
  }

  const resultCount = `${filtered.length} of ${configs.length}`;
  const rowProps = { env, fleet };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Infrastructure"
        title="Modpack Configs"
        icon={Server}
        description="Every modpack server on the network: addresses, ports, clusters, pack versions — and whether players can reach each one right now."
        actions={
          <div className="flex items-center gap-2">
            <PanelTour
              tourId="modpack-configs"
              title="Modpack Configs"
              steps={modpackConfigsPanelTour(perms.canWrite, perms.canAdmin)}
              enabled={perms.canView && !loading}
              {...tourNavigation}
            />
            <Link to="/status-monitor" className="glass-button" title="The status monitor: TPS, backups, hosts and heartbeats">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Status</span>
            </Link>
            {perms.canWrite && <Link to="/modpack-configs/provisioning" className="glass-button" title="Continue shared provisioning sessions"><History className="w-4 h-4" /><span className="hidden sm:inline">Provisioning</span></Link>}
            <button type="button" onClick={() => { load(); void fleet.refresh(); }} className="glass-button" title="Refresh" aria-label="Refresh">
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            {perms.canWrite && (
              <button type="button" onClick={() => setCreating(true)} className="btn-primary" data-tour="configs-launch">
                <Plus className="w-4 h-4" />
                Launch a pack
              </button>
            )}
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2" data-tour="configs-environment">
            <div className="segmented max-w-full">
              <button
                type="button"
                className="segmented-item"
                data-active={env === 'production'}
                onClick={() => setEnv('production')}
              >
                Production
              </button>
              <button
                type="button"
                className="segmented-item"
                data-active={env === 'development'}
                onClick={() => setEnv('development')}
              >
                Development
              </button>
            </div>
            <div className="segmented max-w-full">
              <button
                type="button"
                className="segmented-item"
                data-active={grouping === 'cluster'}
                onClick={() => setGrouping('cluster')}
              >
                <Layers className="w-4 h-4" />
                By cluster
              </button>
              <button
                type="button"
                className="segmented-item"
                data-active={grouping === 'flat'}
                onClick={() => setGrouping('flat')}
              >
                Flat list
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2" data-tour="configs-search">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search address, pack, cluster, region, author, AMP instance…"
                className="glass-input pl-9 w-full"
              />
            </div>
            <select value={cluster} onChange={(e) => setCluster(e.target.value)} className="glass-select">
              <option value={ALL}>All clusters</option>
              {(vocabularies?.clusters ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || `Cluster ${c.id}`} ({c.members})
                </option>
              ))}
            </select>
            <select value={region} onChange={(e) => setRegion(e.target.value)} className="glass-select">
              <option value={ALL}>All regions</option>
              {(vocabularies?.regions ?? []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={modloader} onChange={(e) => setModloader(e.target.value)} className="glass-select">
              <option value={ALL}>All loaders</option>
              {(vocabularies?.modloaders ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={live} onChange={(e) => setLive(e.target.value as LiveFilter)} className="glass-select" aria-label="Filter by live status">
              <option value={ALL}>Any status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="maintenance">In maintenance</option>
              <option value="coming_soon">Coming soon</option>
              <option value="unlisted">Not in server list</option>
              <option value="visible">Visible to players</option>
              <option value="hidden">Hidden from players</option>
            </select>
            <span className="text-xs text-glass-400 tabular-nums md:ml-auto">{resultCount}</span>
          </div>
        </div>
      </PageHeader>

      {env === 'development' && (
        <div className="alert alert-warning" role="status">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>
            You are looking at <strong>servers_dev</strong>. Changes here do not affect live servers.
            {perms.canAdmin && ' Syncing replaces this table entirely with a copy of production.'}
          </span>
          {perms.canAdmin && (
            <button
              type="button"
              onClick={() => setSyncConfirm(true)}
              className="glass-button-secondary ml-auto shrink-0"
            >
              <Database className="w-4 h-4" />
              Sync from production
            </button>
          )}
        </div>
      )}

      {notice && (
        <div className="alert alert-info" role="status">
          <Database className="w-4 h-4 shrink-0" />
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="btn-ghost ml-auto shrink-0">
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Servers"
          value={stats.total}
          icon={Server}
          loading={loading}
          hint={stats.hidden > 0 ? `${stats.hidden} hidden from players` : 'all visible to players'}
          onClick={stats.hidden > 0 ? () => setLive(live === 'hidden' ? ALL : 'hidden') : undefined}
        />
        <StatTile label="Clusters" value={stats.clusters} icon={Layers} tone="accent" loading={loading} />
        <StatTile
          label="Online"
          value={stats.loaded ? stats.online : '—'}
          icon={Activity}
          tone={stats.loaded && stats.offline > 0 ? 'warning' : 'success'}
          loading={loading}
          hint={stats.loaded ? `${stats.offline} offline · ${stats.listed} in the server list` : 'checking the server list…'}
          onClick={() => setLive(live === 'offline' ? ALL : 'offline')}
        />
        <StatTile
          label="In maintenance"
          value={stats.loaded ? stats.maintenance : '—'}
          icon={TriangleAlert}
          tone={stats.maintenance > 0 ? 'warning' : 'info'}
          loading={loading}
          hint={[stats.maintenance > 0 ? 'down on purpose' : 'nothing down on purpose',
            stats.comingSoon > 0 && `${stats.comingSoon} coming soon`].filter(Boolean).join(' · ')}
          onClick={() => setLive(live === 'maintenance' ? ALL : 'maintenance')}
        />
      </div>

      {loading ? (
        <ListSkeleton />
      ) : error ? (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load modpack configs"
          description={error}
          action={<button type="button" onClick={load} className="glass-button">Retry</button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title={configs.length === 0 ? 'No modpack configs yet' : 'Nothing matches those filters'}
          description={
            configs.length === 0
              ? 'Packs launched from here or from Mr. Roboto will appear in this list.'
              : 'Try a broader search or clear a filter.'
          }
        />
      ) : (
        <>
          {grouping === 'flat' ? (
            <>
              <div className="hidden md:block table-wrap max-h-[70vh]" data-tour="configs-list">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Pack</th>
                      <th className="th">Cluster</th>
                      <th className="th">Region</th>
                      <th className="th">Version</th>
                      <th className="th">Loader</th>
                      <th className="th">Live</th>
                      <th className="th">Last wipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((config) => <ConfigRow key={config.id} config={config} {...rowProps} />)}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden space-y-3">
                {filtered.map((config) => <ConfigCard key={config.id} config={config} {...rowProps} />)}
              </div>
            </>
          ) : (
            <>
              <div className="hidden md:block table-wrap max-h-[70vh]" data-tour="configs-list">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Pack</th>
                      <th className="th">Cluster</th>
                      <th className="th">Region</th>
                      <th className="th">Version</th>
                      <th className="th">Loader</th>
                      <th className="th">Live</th>
                      <th className="th">Last wipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.clusters.map((group) => (
                      <ClusterRows key={group.parent} cluster={group} actions={clusterActions} {...rowProps} />
                    ))}
                    {grouped.standalone.length > 0 && (
                      <>
                        <tr>
                          <td colSpan={7} className="td bg-dark-900/30">
                            <div className="flex items-center gap-2">
                              <span className="badge badge-neutral">Standalone</span>
                              <span className="text-xs text-glass-400 tabular-nums">
                                {grouped.standalone.length} server{grouped.standalone.length === 1 ? '' : 's'} not in a cluster
                              </span>
                            </div>
                          </td>
                        </tr>
                        {grouped.standalone.map((config) => (
                          <ClusterRows
                            key={config.id}
                            cluster={{ parent: config.id, name: config.modpack_full_name || config.server_address, head: config, members: [config], versions: [config.modpack_version ?? '—'] }}
                            actions={clusterActions}
                            {...rowProps}
                          />
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-4">
                {grouped.clusters.map((group) => (
                  <div key={group.parent} className="space-y-2">
                    <ClusterHeading cluster={group} actions={clusterActions} fleet={fleet} />
                    {group.members.map((config) => <ConfigCard key={config.id} config={config} {...rowProps} />)}
                  </div>
                ))}
                {grouped.standalone.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1">
                      <span className="badge badge-neutral">Standalone</span>
                      <span className="text-xs text-glass-400 tabular-nums">{grouped.standalone.length}</span>
                    </div>
                    {grouped.standalone.map((config) => <ConfigCard key={config.id} config={config} {...rowProps} />)}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {creating && (
        <CreateModpackConfigModal
          env={env}
          vocabularies={vocabularies}
          existing={configs}
          canAdmin={perms.canAdmin}
          onClose={() => setCreating(false)}
          onSuccess={async (address) => {
            setCreating(false);
            setNotice(`${address} created.`);
            await load();
          }}
        />
      )}

      {updating && updating.head && (
        <UpdatePackModal
          config={updating.head}
          members={updating.members}
          env={env}
          fleet={fleet}
          onClose={() => setUpdating(null)}
          onUpdated={load}
        />
      )}

      {wiping && wiping.head && (
        <RecordWipeModal
          config={wiping.head}
          members={wiping.members}
          env={env}
          onClose={() => setWiping(null)}
          onRecorded={load}
        />
      )}

      {announcingLaunch && announcingLaunch.head && (
        <AnnounceLaunchModal
          config={announcingLaunch.head}
          members={announcingLaunch.members}
          env={env}
          onClose={() => setAnnouncingLaunch(null)}
        />
      )}

      {syncConfirm && (
        <ConfirmationModal
          onClose={() => setSyncConfirm(false)}
          onConfirm={handleSync}
          isLoading={syncing}
          title="Replace the development table?"
          message={
            'This deletes every row in servers_dev and replaces it with a copy of production. ' +
            'Anything only in development is lost. Live servers are not affected.'
          }
          confirmText="Replace development"
        />
      )}
    </div>
  );
};

const Dash = () => <span className="text-glass-500">—</span>;

interface ClusterGroup {
  parent: number;
  name: string | null;
  head: ModpackConfig | null;
  members: ModpackConfig[];
  versions: string[];
}

interface ClusterActions {
  onUpdate?: (cluster: ClusterGroup) => void;
  onWipe?: (cluster: ClusterGroup) => void;
  onAnnounceLaunch?: (cluster: ClusterGroup) => void;
}

interface RowProps {
  env: ModpackConfigEnv;
  fleet: FleetStatus;
}

/** Where this cluster lives on the status page: by cluster name, or by address for a standalone pack. */
const statusPath = (cluster: ClusterGroup) =>
  cluster.name && cluster.name !== UNCLUSTERED && cluster.members.length > 1
    ? statusMonitorPathFor({ cluster: cluster.name })
    : statusMonitorPathFor({ address: cluster.head?.server_address ?? cluster.members[0]?.server_address });

const ClusterHeading = ({ cluster, actions, fleet }: { cluster: ClusterGroup; actions?: ClusterActions; fleet: FleetStatus }) => {
  const lookups = cluster.members.map((m) => liveFor(fleet, m.server_address));
  const hiddenCount = cluster.members.filter(isHidden).length;
  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <Layers className="w-4 h-4 text-accent-300 shrink-0" />
      <span className="font-semibold text-white truncate">{cluster.name || `Cluster ${cluster.parent}`}</span>
      <span className="text-xs text-glass-400 tabular-nums">
        {cluster.members.length} server{cluster.members.length === 1 ? '' : 's'}
      </span>
      {cluster.head ? (
        <span className="text-xs text-glass-400 font-mono truncate">head {cluster.head.server_address}</span>
      ) : (
        <span className="badge badge-warning" title="No member is self-parented, so nothing heads this cluster">No head</span>
      )}
      {cluster.versions.length > 1 && (
        <span className="badge badge-warning" title={`Members are on ${cluster.versions.join(', ')}`}>
          Mixed versions
        </span>
      )}
      {hiddenCount > 0 && (
        <span className="badge badge-warning inline-flex items-center gap-1" title="Withdrawn from the public API">
          <EyeOff className="w-3 h-3" />
          {hiddenCount === cluster.members.length ? 'Hidden' : `${hiddenCount} hidden`}
        </span>
      )}
      <ClusterLiveSummary lookups={lookups} />
      {cluster.head && <LastWipe config={cluster.head} className="text-glass-400" />}
      <Link
        to={statusPath(cluster)}
        className="text-xs text-glass-400 hover:text-white inline-flex items-center gap-1"
        title="TPS, backups, hosts and heartbeats for these servers"
      >
        <Activity className="w-3 h-3" />
        Status
      </Link>
      {(actions?.onUpdate || actions?.onWipe || actions?.onAnnounceLaunch) && (
        <span className="ml-auto flex items-center gap-1.5">
          {actions.onWipe && cluster.head && (
            <button
              type="button"
              onClick={() => actions.onWipe!(cluster)}
              className="btn-xs glass-button-secondary"
              title="Record when the world was last wiped — players are shown it"
              data-tour="configs-wipe"
            >
              <History className="w-3 h-3" />
              Record wipe
            </button>
          )}
          {actions.onUpdate && (
            <button
              type="button"
              onClick={() => actions.onUpdate!(cluster)}
              className="btn-xs glass-button-secondary"
              title="Record a new version on every server here and announce it"
              data-tour="configs-update"
            >
              <ArrowUpCircle className="w-3 h-3" />
              Update pack
            </button>
          )}
          {actions.onAnnounceLaunch && cluster.head && (
            <button
              type="button"
              onClick={() => actions.onAnnounceLaunch!(cluster)}
              className="btn-xs glass-button-secondary"
              title={cluster.members.length > 1 ? 'Post the launch announcement, naming every server in the cluster' : 'Post the launch announcement for this pack'}
              data-tour="configs-announce-launch"
            >
              <Megaphone className="w-3 h-3" />
              Announce launch
            </button>
          )}
        </span>
      )}
    </div>
  );
};

const ClusterRows = ({ cluster, env, fleet, actions }: { cluster: ClusterGroup; actions?: ClusterActions } & RowProps) => (
  <>
    <tr>
      <td colSpan={7} className="td bg-dark-900/30">
        <ClusterHeading cluster={cluster} actions={actions} fleet={fleet} />
      </td>
    </tr>
    {cluster.members.map((config) => <ConfigRow key={config.id} config={config} env={env} fleet={fleet} />)}
  </>
);

const ConfigRow = ({ config, env, fleet }: { config: ModpackConfig } & RowProps) => (
  <tr className={cn('row-active', isHidden(config) && 'opacity-75')}>
    <td className="td">
      <Link to={detailPath(config, env)} className="link block min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          <span className="truncate font-medium text-white">
            {config.modpack_full_name || config.modpack_name || config.server_address}
          </span>
          <HiddenBadge config={config} className="shrink-0" />
        </span>
        <span className="block truncate text-xs text-glass-400 font-mono">
          {config.server_address}
        </span>
      </Link>
    </td>
    <td className="td"><ClusterCell config={config} /></td>
    <td className="td">{config.region_name || <Dash />}</td>
    <td className="td font-mono tabular-nums">{config.modpack_version || <Dash />}</td>
    <td className="td">{config.modloader || <Dash />}</td>
    <td className="td"><LiveStatusBadge live={liveFor(fleet, config.server_address)} withDetail /></td>
    <td className="td"><LastWipe config={config} showNever /></td>
  </tr>
);

const ConfigCard = ({ config, env, fleet }: { config: ModpackConfig } & RowProps) => (
  <Link to={detailPath(config, env)} className={cn('glass-card glass-card-interactive p-4 block', isHidden(config) && 'opacity-75')}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-white truncate">
          {config.modpack_full_name || config.modpack_name || config.server_address}
        </p>
        <p className="text-xs text-glass-400 font-mono truncate">{config.server_address}</p>
      </div>
      <LiveStatusBadge live={liveFor(fleet, config.server_address)} className="shrink-0" />
    </div>
    <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-glass-400">
      <ClusterCell config={config} />
      <HiddenBadge config={config} />
      {config.region_name && <span className="badge badge-neutral">{config.region_name}</span>}
      {config.modloader && <span className="badge badge-neutral">{config.modloader}</span>}
      {config.modpack_version && <span className="font-mono tabular-nums">{config.modpack_version}</span>}
      <LastWipe config={config} className="ml-auto" />
    </div>
  </Link>
);

const detailPath = (config: ModpackConfig, env: ModpackConfigEnv) =>
  `/modpack-configs/${encodeURIComponent(config.server_address)}${env === 'development' ? '?env=development' : ''}`;

const ClusterCell = ({ config }: { config: ModpackConfig }) => {
  if (!config.cluster_name || config.cluster_name === UNCLUSTERED) {
    return <span className="badge badge-neutral">Standalone</span>;
  }
  const isHead = config.cluster_parent === config.id;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className="badge badge-accent truncate">{config.cluster_name}</span>
      {isHead && <span className="badge badge-purple" title="Heads this cluster">Head</span>}
    </span>
  );
};

const ListSkeleton = () => (
  <div className="table-wrap">
    <div className="p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <SkeletonLoader className="h-9 flex-1" />
          <SkeletonLoader className="h-9 w-24 hidden md:block" />
          <SkeletonLoader className="h-9 w-16 hidden md:block" />
          <SkeletonLoader className="h-9 w-20 hidden md:block" />
        </div>
      ))}
    </div>
  </div>
);
